import { useEffect, useState } from 'react';
import Button from '@alltrails/shared/denali/components/Button';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { loadRecaptcha, executeRecaptcha } from 'utils/recaptcha_helpers';
import { validateUserForm } from 'utils/UserFormHelper';
import { defineMessages, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import Checkbox from '@alltrails/shared/denali/components/Checkbox';
import Typography from '@alltrails/shared/denali/components/Typography';
import OutlinedTextField from '@alltrails/shared/components/OutlinedTextField';
import * as formStyles from 'components/forms/styles/styles.module.scss';
import useEnableRecaptcha from 'hooks/useEnableRecaptcha';
import useCountryId from 'hooks/useCountryId';
import * as styles from './RegisterForm.module.scss';
import { emailSignupHandler } from '../../utils/AuthHandlers';

const messages = defineMessages({
  signUp: {
    defaultMessage: 'Sign up'
  },
  errorTryAgain: {
    defaultMessage: 'Error, please try again'
  }
});

type Props = {
  autoFocus?: boolean;
  referralLink?: string;
  handleRegistrationSuccess: any;
  userEmailSubscribed?: boolean;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6514%3A101082
export default function RegisterForm({ autoFocus = false, referralLink, handleRegistrationSuccess, userEmailSubscribed }: Props) {
  const {
    formattedDefaultMessages: { signUp, errorTryAgain }
  } = useFormatMessage(messages);

  const intl = useIntl();
  const countryId = useCountryId();
  const enableRecaptcha = useEnableRecaptcha();

  const [disableSubmit, setDisableSubmit] = useState<boolean>(enableRecaptcha);
  const [recaptchaObject, setRecaptchaObject] = useState<any>();
  const [serverError, setServerError] = useState();

  const [state, setState] = useState<{
    userEmailSubscribed: boolean;
    userRegLastName: string;
    userRegEmail: string;
    userRegPassword: string;
    userRegFirstName: string;
    submitting: boolean;
    showErrorsOnType: boolean;
  }>({
    userRegEmail: '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed,
    submitting: false,
    showErrorsOnType: false
  });

  const locale = useLanguageRegionCode();

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha((recaptchaObject: any) => {
        setRecaptchaObject(recaptchaObject);
        setDisableSubmit(false);
      });
    }
  }, [enableRecaptcha]);

  const [errors, setErrors] = useState<{ userRegEmail?: string; userRegPassword?: string; userRegFirstName?: string; userRegLastName?: string }>({});

  function showEmailSubscribed() {
    return countryId !== 313;
  }

  function handleSubscribedChange() {
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    e.preventDefault();
    const { name, value } = e.target;
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors, intl).errors);
    }

    setState({ ...state, [name]: value });
  }

  function handleSuccessCallback(data: any) {
    setErrors({});

    setState({
      userRegEmail: '',
      userRegPassword: '',
      userRegFirstName: '',
      userRegLastName: '',
      userEmailSubscribed,
      submitting: false,
      showErrorsOnType: false
    });
    setServerError(null);
    setRecaptchaObject(null);
    setDisableSubmit(enableRecaptcha);

    if (handleRegistrationSuccess) {
      handleRegistrationSuccess(data);
    }
  }

  function handleErrorCallback(err: { responseJSON: { errors: any[] } }) {
    const serverResponseError = err && err.responseJSON ? err.responseJSON.errors[0] : { message: errorTryAgain };

    if (serverResponseError && serverResponseError.target === 'email') {
      setErrors({ ...errors, userRegEmail: serverResponseError.message });
      setServerError(serverResponseError.message);
    } else if (serverResponseError && serverResponseError.message) {
      setServerError(serverResponseError.message);
    }
    setState({ ...state, submitting: false });
  }

  function submitRegisterHelper(recaptchaToken?: string) {
    emailSignupHandler(
      {
        first_name: state.userRegFirstName,
        last_name: state.userRegLastName,
        email: state.userRegEmail,
        password: state.userRegPassword,
        create_session: true,
        subscribed: state.userEmailSubscribed,
        recaptcha: recaptchaToken,
        referral_code: referralLink
      },
      locale,
      handleSuccessCallback,
      handleErrorCallback,
      (): void => null
    );
  }

  function submitRegister(e: React.KeyboardEvent<HTMLDivElement> | React.FormEvent) {
    e.preventDefault();
    if (disableSubmit || state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors, intl).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });
      return;
    }

    setState({ ...state, submitting: true });
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (enableRecaptcha) {
      executeRecaptcha(recaptchaObject, 'userSignupLogin').then(
        (recaptchaToken: any) => submitRegisterHelper(recaptchaToken),
        () => setState({ ...state, submitting: false })
      );
    } else {
      submitRegisterHelper();
    }
  }

  const disabled = disableSubmit || state.submitting;

  return (
    <form className={styles.form} onSubmit={submitRegister}>
      <OutlinedTextField
        autoFocus={autoFocus}
        name="userRegFirstName"
        errorMessage={errors.userRegFirstName}
        value={state.userRegFirstName}
        label={<FormattedMessage defaultMessage="First name" />}
        changeHandler={handleChange}
        data-testid="registrationForm-firstNameTextField"
      />
      <OutlinedTextField
        name="userRegLastName"
        errorMessage={errors.userRegLastName}
        value={state.userRegLastName}
        label={<FormattedMessage defaultMessage="Last name" />}
        changeHandler={handleChange}
        data-testid="registrationForm-lastNameTextField"
      />
      <OutlinedTextField
        name="userRegEmail"
        errorMessage={errors.userRegEmail}
        value={state.userRegEmail}
        label={<FormattedMessage defaultMessage="Email address" />}
        changeHandler={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (serverError) {
            setServerError(null);
          }
          if (errors.userRegEmail) {
            setErrors({ userRegEmail: '' });
          }
          handleChange(e);
        }}
        type="email"
        data-testid="registrationForm-emailTextField"
      />
      <OutlinedTextField
        name="userRegPassword"
        errorMessage={errors.userRegPassword}
        value={state.userRegPassword}
        label={<FormattedMessage defaultMessage="Password" />}
        changeHandler={handleChange}
        type="password"
        data-testid="registrationForm-passwordTextField"
      />
      {serverError && <div className={`${formStyles.serverError} ${styles.center}`}>{serverError}</div>}
      <div className={styles.bottom}>
        <Button
          text={signUp}
          disabled={disabled}
          onClick={() => {
            /* do nothing - form submit handles sign up */
          }}
          testId="register-form-sign-up"
          type="submit"
          variant="primary"
          width="full"
        />
        {showEmailSubscribed() && (
          <Checkbox
            className={styles.subscribedContainer}
            id="emailSubscribed"
            testId="checkbox-emailSubscribed"
            size="sm"
            onChange={handleSubscribedChange}
            selected={state.userEmailSubscribed}
            labelElement={
              <Typography variant="text200">
                <FormattedMessage defaultMessage="Get helpful product tips and special offers" />
              </Typography>
            }
          />
        )}
      </div>
    </form>
  );
}
